import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { usePartners, Partner } from "@/context/PartnerContext";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/layouts/PageHeader";
import { StatCard } from "@/components/ui/StatCard";
import { Progress } from "@/components/ui/progress";
import { EmptyState } from "@/components/ui/EmptyState";
import {
  Check,
  Edit,
  MapPin,
  MoreVertical,
  Plus,
  Search,
  Trash,
  X,
  HeartHandshake,
  Map,
  ArrowRight,
  Mail,
  UserPlus,
  Phone,
  Users,
  TrendingUp,
  Copy,
  AlertTriangle,
  ShieldAlert,
  Gauge,
  Activity,
  BarChart3,
} from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { ServiceType, useLeads } from "@/context/LeadContext";
import ServiceBadge from "@/components/ui-custom/ServiceBadge";
import { Loader2 } from "lucide-react";

type PartnerOpsStats = {
  leads: number;
  success: number;
  conversion: number;
  pending: number;
  info: number;
  fresh: number;
  stalePending: number;
  recent7d: number;
  viewRate: number;
  healthScore: number;
  lastLeadAt: string | null;
  needsAttention: boolean;
  reasons: string[];
};

const SERVICE_OPTIONS: ServiceType[] = [
  "εξοικονομώ",
  "αντλίεςΘερμότητας",
  "φωτοβολταϊκά",
  "κλιματιστικά",
  "φυσικόΑέριο",
  "ηλιακά",
  "άλλο",
];

const SERVICE_LABELS: Record<ServiceType, string> = {
  εξοικονομώ: "Εξοικονομώ",
  αντλίεςΘερμότητας: "Αντλίες Θερμότητας",
  φωτοβολταϊκά: "Φωτοβολταϊκά",
  κλιματιστικά: "Κλιματιστικά",
  φυσικόΑέριο: "Φυσικό Αέριο",
  ηλιακά: "Ηλιακά",
  άλλο: "Άλλο",
};

const Partners: React.FC = () => {
  const { partners, removePartner, refreshPartners } = usePartners();
  const { leads } = useLeads();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [serviceFilter, setServiceFilter] = useState<ServiceType | 'all'>('all');
  const [focusFilter, setFocusFilter] = useState<'all' | 'needsAttention' | 'topPerformers' | 'dormant'>('all');
  const [sortBy, setSortBy] = useState<'leadsDesc' | 'conversionDesc' | 'scoreDesc' | 'attentionDesc' | 'nameAsc'>('leadsDesc');
  const [partnerToDelete, setPartnerToDelete] = useState<Partner | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showRegLinkDialog, setShowRegLinkDialog] = useState(false);

  const partnerStatsById = useMemo(() => {
    const stats: Record<string, PartnerOpsStats> = {};
    const now = Date.now();
    const oneWeekAgo = now - (7 * 24 * 60 * 60 * 1000);
    const staleLimit = now - (48 * 60 * 60 * 1000);

    partners.forEach(partner => {
      const partnerLeads = leads.filter(lead => lead.partnerId === partner.id);
      const success = partnerLeads.filter(lead => lead.stage === "έκλεισεΗΔουλειά").length;
      const pending = partnerLeads.filter(lead => lead.stage === "σεΕκκρεμότητα").length;
      const info = partnerLeads.filter(lead => lead.stage === "ήθελεΠληροφορίες").length;
      const fresh = partnerLeads.filter(lead => lead.stage === "Νέο").length;
      const conversion = partnerLeads.length > 0 ? Math.round((success / partnerLeads.length) * 100) : 0;

      const viewed = partnerLeads.filter(lead => lead.viewed).length;
      const viewRate = partnerLeads.length > 0 ? Math.round((viewed / partnerLeads.length) * 100) : 0;

      const stalePending = partnerLeads.filter(lead => {
        if (lead.stage !== "σεΕκκρεμότητα") return false;
        const referenceDate = new Date(lead.updatedAt || lead.createdAt).getTime();
        return referenceDate <= staleLimit;
      }).length;

      const recent7d = partnerLeads.filter(lead => {
        const created = new Date(lead.createdAt).getTime();
        return created >= oneWeekAgo;
      }).length;

      const sortedByRecent = [...partnerLeads].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      const lastLeadAt = sortedByRecent[0]?.createdAt || null;

      const reasons: string[] = [];
      if (partner.active && !partner.email) reasons.push("Χωρίς email επικοινωνίας");
      if (partner.active && partner.sms_opt_out) reasons.push("SMS opt-out ενεργό");
      if (partnerLeads.length >= 5 && conversion < 20) reasons.push("Χαμηλό conversion");
      if (stalePending >= 2) reasons.push("Παλιά εκκρεμή leads");
      if (partnerLeads.length >= 5 && viewRate < 35) reasons.push("Χαμηλό view rate");
      if (partner.active && partnerLeads.length > 0 && recent7d === 0) reasons.push("Καμία νέα ανάθεση 7 ημερών");

      const rawHealth =
        45 +
        (conversion * 0.35) +
        (viewRate * 0.2) +
        Math.min(recent7d * 4, 20) -
        (stalePending * 10) -
        (partner.sms_opt_out ? 8 : 0) -
        (!partner.email ? 8 : 0);

      const healthScore = Math.max(0, Math.min(100, Math.round(rawHealth)));

      stats[partner.id] = {
        leads: partnerLeads.length,
        success,
        conversion,
        pending,
        info,
        fresh,
        stalePending,
        recent7d,
        viewRate,
        healthScore,
        lastLeadAt,
        needsAttention: reasons.length > 0,
        reasons,
      };
    });

    return stats;
  }, [leads, partners]);

  const filteredPartners = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    let result = partners.filter((partner) => {
      const partnerMatch =
        !normalizedSearch ||
        partner.name.toLowerCase().includes(normalizedSearch) ||
        partner.phone.includes(normalizedSearch) ||
        partner.location.toLowerCase().includes(normalizedSearch) ||
        (partner.email || '').toLowerCase().includes(normalizedSearch);

      const serviceMatch =
        serviceFilter === 'all' ||
        partner.services.includes(serviceFilter);

      const attention = partnerStatsById[partner.id]?.needsAttention ?? false;
      const conversion = partnerStatsById[partner.id]?.conversion ?? 0;
      const leadsCount = partnerStatsById[partner.id]?.leads ?? 0;

      const focusMatch =
        focusFilter === 'all' ||
        (focusFilter === 'needsAttention' && attention) ||
        (focusFilter === 'topPerformers' && conversion >= 40 && leadsCount >= 5) ||
        (focusFilter === 'dormant' && leadsCount > 0 && (partnerStatsById[partner.id]?.recent7d ?? 0) === 0);

      return partnerMatch && serviceMatch && focusMatch;
    });

    result = [...result].sort((a, b) => {
      if (sortBy === 'nameAsc') {
        return a.name.localeCompare(b.name, 'el');
      }

      if (sortBy === 'scoreDesc') {
        const aScore = partnerStatsById[a.id]?.healthScore ?? 0;
        const bScore = partnerStatsById[b.id]?.healthScore ?? 0;
        return bScore - aScore;
      }

      if (sortBy === 'attentionDesc') {
        const aRisk = (partnerStatsById[a.id]?.reasons.length ?? 0) + (partnerStatsById[a.id]?.stalePending ?? 0);
        const bRisk = (partnerStatsById[b.id]?.reasons.length ?? 0) + (partnerStatsById[b.id]?.stalePending ?? 0);
        return bRisk - aRisk;
      }

      if (sortBy === 'conversionDesc') {
        const aConversion = partnerStatsById[a.id]?.conversion ?? 0;
        const bConversion = partnerStatsById[b.id]?.conversion ?? 0;
        return bConversion - aConversion;
      }

      const aLeads = partnerStatsById[a.id]?.leads ?? 0;
      const bLeads = partnerStatsById[b.id]?.leads ?? 0;
      return bLeads - aLeads;
    });

    return result;
  }, [focusFilter, partnerStatsById, partners, searchTerm, serviceFilter, sortBy]);

  const activePartners = filteredPartners.filter((partner) => partner.active);
  const inactivePartners = filteredPartners.filter((partner) => !partner.active);

  const partnersSummary = useMemo(() => {
    const active = partners.filter(partner => partner.active).length;
    const attention = partners.filter(partner => partnerStatsById[partner.id]?.needsAttention).length;
    const dormant = partners.filter(partner => {
      const s = partnerStatsById[partner.id];
      return partner.active && (s?.leads ?? 0) > 0 && (s?.recent7d ?? 0) === 0;
    }).length;
    const topPerformers = partners.filter(partner => {
      const s = partnerStatsById[partner.id];
      return (s?.conversion ?? 0) >= 40 && (s?.leads ?? 0) >= 5;
    }).length;
    const avgConversion = partners.length > 0
      ? Math.round(partners.reduce((acc, p) => acc + (partnerStatsById[p.id]?.conversion ?? 0), 0) / partners.length)
      : 0;
    const avgHealth = partners.length > 0
      ? Math.round(partners.reduce((acc, p) => acc + (partnerStatsById[p.id]?.healthScore ?? 0), 0) / partners.length)
      : 0;

    const stalePending = partners.reduce((acc, partner) => acc + (partnerStatsById[partner.id]?.stalePending ?? 0), 0);

    return { active, attention, dormant, topPerformers, avgConversion, avgHealth, stalePending };
  }, [partnerStatsById, partners]);

  const attentionQueue = useMemo(() => {
    return partners
      .filter(partner => partner.active && (partnerStatsById[partner.id]?.needsAttention ?? false))
      .sort((a, b) => {
        const aScore = (partnerStatsById[a.id]?.reasons.length ?? 0) * 10 + (partnerStatsById[a.id]?.stalePending ?? 0) * 8;
        const bScore = (partnerStatsById[b.id]?.reasons.length ?? 0) * 10 + (partnerStatsById[b.id]?.stalePending ?? 0) * 8;
        return bScore - aScore;
      })
      .slice(0, 5);
  }, [partnerStatsById, partners]);

  const serviceCoverage = useMemo(() => {
    return SERVICE_OPTIONS.map((service) => {
      const activeCount = partners.filter(partner => partner.active && partner.services.includes(service)).length;
      return {
        service,
        label: SERVICE_LABELS[service],
        activeCount,
      };
    });
  }, [partners]);

  const handleDeletePartner = async () => {
    if (!partnerToDelete) return;

    setIsDeleting(true);
    try {
      await removePartner(partnerToDelete.id);
      setPartnerToDelete(null);
      toast({
        title: "Επιτυχής διαγραφή!",
        description: "Ο συνεργάτης διαγράφηκε επιτυχώς.",
      });
    } catch (error) {
      console.error("Error deleting partner:", error);
      toast({
        title: "Σφάλμα",
        description: error instanceof Error ? error.message : "Υπήρξε πρόβλημα κατά τη διαγραφή",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const copyRegistrationLink = () => {
    const registrationURL = `${window.location.origin}/partner/register`;
    navigator.clipboard.writeText(registrationURL);
    toast({
      title: "Αντιγράφηκε!",
      description: "Ο σύνδεσμος εγγραφής αντιγράφηκε στο πρόχειρο."
    });
  };

  // Stats at a glance
  const totalLeads = leads.length;
  const totalSuccess = leads.filter(l => l.stage === "έκλεισεΗΔουλειά").length;

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Page Header */}
      <PageHeader
        title="Συνεργάτες"
        description="Διαχείριση δικτύου συνεργατών"
        icon={<HeartHandshake />}
        iconGradient="blue"
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => setShowRegLinkDialog(true)}
              className="border-slate-200 hover:bg-slate-50"
              aria-label="Εμφάνιση συνδέσμου εγγραφής"
            >
              <UserPlus className="mr-2 h-4 w-4" /> Σύνδεσμος Εγγραφής
            </Button>
            <Button asChild variant="outline" className="border-slate-200 hover:bg-slate-50">
              <Link to="/admin/partners/map">
                <Map className="mr-2 h-4 w-4" /> Χάρτης
              </Link>
            </Button>
            <Button asChild variant="gradient">
              <Link to="/admin/partners/new">
                <Plus className="mr-2 h-4 w-4" /> Νέος Συνεργάτης
              </Link>
            </Button>
          </>
        }
      />

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard
          value={partners.length}
          title="Συνολικά"
          icon={<Users />}
          variant="outlined"
          size="compact"
        />
        <StatCard
          value={partnersSummary.active}
          title="Ενεργοί"
          icon={<Check />}
          variant="outlined"
          size="compact"
        />
        <StatCard
          value={partnersSummary.avgHealth}
          title="Network Health"
          icon={<Gauge />}
          variant="outlined"
          size="compact"
        />
        <StatCard
          value={totalLeads}
          title="Σύν. Leads"
          icon={<HeartHandshake />}
          variant="outlined"
          size="compact"
        />
        <StatCard
          value={partnersSummary.stalePending}
          title="Παλιά Εκκρεμή"
          icon={<ShieldAlert />}
          variant="outlined"
          size="compact"
        />
        <StatCard
          value={totalSuccess}
          title="Επιτυχίες"
          icon={<TrendingUp />}
          variant="outlined"
          size="compact"
        />
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Αναζήτηση συνεργατών..."
                className="pl-10 bg-white border-slate-200 focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  onClick={() => setSearchTerm("")}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            <Select value={serviceFilter} onValueChange={(value) => setServiceFilter(value as ServiceType | 'all')}>
              <SelectTrigger className="w-full lg:w-52">
                <SelectValue placeholder="Υπηρεσία" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Όλες οι Υπηρεσίες</SelectItem>
                <SelectItem value="εξοικονομώ">Εξοικονομώ</SelectItem>
                <SelectItem value="αντλίεςΘερμότητας">Αντλίες Θερμότητας</SelectItem>
                <SelectItem value="φωτοβολταϊκά">Φωτοβολταϊκά</SelectItem>
                <SelectItem value="κλιματιστικά">Κλιματιστικά</SelectItem>
                <SelectItem value="φυσικόΑέριο">Φυσικό Αέριο</SelectItem>
                <SelectItem value="ηλιακά">Ηλιακά</SelectItem>
                <SelectItem value="άλλο">Άλλο</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={(value) => setSortBy(value as 'leadsDesc' | 'conversionDesc' | 'scoreDesc' | 'attentionDesc' | 'nameAsc')}>
              <SelectTrigger className="w-full lg:w-52">
                <SelectValue placeholder="Ταξινόμηση" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="leadsDesc">Περισσότερα leads</SelectItem>
                <SelectItem value="conversionDesc">Υψηλότερο conversion</SelectItem>
                <SelectItem value="scoreDesc">Υψηλότερο health score</SelectItem>
                <SelectItem value="attentionDesc">Υψηλότερο ρίσκο</SelectItem>
                <SelectItem value="nameAsc">Αλφαβητικά</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-wrap gap-2">
            {([
              { key: 'all' as const,           label: 'Όλοι',                  count: partners.length,              icon: null,          alert: false },
              { key: 'needsAttention' as const, label: 'Χρειάζονται προσοχή',  count: partnersSummary.attention,    icon: AlertTriangle,  alert: partnersSummary.attention > 0 },
              { key: 'topPerformers' as const,  label: 'Top performers',         count: partnersSummary.topPerformers, icon: null,         alert: false },
              { key: 'dormant' as const,        label: 'Dormant',               count: partnersSummary.dormant,      icon: Activity,       alert: partnersSummary.dormant > 0 },
            ] as const).map(({ key, label, count, icon: Icon, alert }) => (
              <button
                key={key}
                onClick={() => setFocusFilter(key)}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-all ${
                  focusFilter === key
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {Icon && <Icon className="h-3.5 w-3.5" />}
                {label}
                <span className={`rounded-full px-1.5 py-0.5 text-xs font-semibold ${
                  focusFilter === key ? 'bg-white/20 text-white' : alert ? 'bg-amber-100 text-amber-700' : 'bg-white text-slate-600'
                }`}>{count}</span>
              </button>
            ))}
            {(searchTerm || serviceFilter !== 'all' || focusFilter !== 'all' || sortBy !== 'leadsDesc') && (
              <button
                onClick={() => { setSearchTerm(''); setServiceFilter('all'); setFocusFilter('all'); setSortBy('leadsDesc'); }}
                className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 transition-colors"
              >
                <X className="h-3.5 w-3.5" /> Καθαρισμός
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Queue Προσοχής</CardTitle>
            <CardDescription>Οι συνεργάτες με μεγαλύτερο λειτουργικό ρίσκο</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {attentionQueue.length === 0 ? (
              <p className="text-sm text-slate-500">Δεν υπάρχουν συνεργάτες που χρειάζονται άμεση προσοχή.</p>
            ) : (
              attentionQueue.map((partner) => {
                const stats = partnerStatsById[partner.id];
                return (
                  <div key={partner.id} className="rounded-lg border border-slate-200 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-medium text-slate-900">{partner.name}</p>
                        <p className="text-xs text-slate-500">{stats?.leads ?? 0} leads • {stats?.conversion ?? 0}% conversion • {stats?.viewRate ?? 0}% views</p>
                      </div>
                      <Link to={`/admin/partners/${partner.id}/dashboard`}>
                        <Button size="sm" variant="outline">Άνοιγμα</Button>
                      </Link>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(stats?.reasons ?? []).slice(0, 3).map((reason) => (
                        <Badge key={`${partner.id}-${reason}`} variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                          {reason}
                        </Badge>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Κάλυψη Υπηρεσιών</CardTitle>
            <CardDescription>Ενεργή διαθεσιμότητα συνεργατών ανά υπηρεσία</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2.5">
            {serviceCoverage.map((item) => (
              <div key={item.service} className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="font-medium text-slate-800">{item.label}</span>
                  <Badge
                    variant="outline"
                    className={item.activeCount === 0 ? "bg-red-50 text-red-700 border-red-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}
                  >
                    {item.activeCount} ενεργοί
                  </Badge>
                </div>
                <Progress value={Math.min(100, item.activeCount * 20)} className="h-2" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="active" className="w-full">
        <TabsList className="bg-slate-100 p-1 rounded-lg">
          <TabsTrigger value="active" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
            Ενεργοί ({activePartners.length})
          </TabsTrigger>
          <TabsTrigger value="inactive" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
            Ανενεργοί ({inactivePartners.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-6">
          {activePartners.length === 0 ? (
            <EmptyState
              icon={<Users />}
              title="Δεν βρέθηκαν ενεργοί συνεργάτες"
              description={searchTerm ? "Δοκιμάστε διαφορετικούς όρους αναζήτησης" : undefined}
              action={
                searchTerm ? (
                  <Button variant="ghost" onClick={() => setSearchTerm("")}>
                    Καθαρισμός φίλτρων
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {activePartners.map((partner, index) => (
                <Card
                  key={partner.id}
                  className="group border-slate-200 hover:border-blue-200 hover:shadow-lg transition-all duration-300"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm">
                          {partner.name.split(' ').map((p: string) => p[0]).join('').toUpperCase().slice(0, 2)}
                        </div>
                        <div>
                          <CardTitle className="text-lg">{partner.name}</CardTitle>
                          <CardDescription className="flex items-center gap-1 mt-0.5">
                            <MapPin className="h-3 w-3" />
                            {partner.location}
                          </CardDescription>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            variant="ghost" 
                            className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            aria-label={`Ενέργειες για ${partner.name}`}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Ενέργειες</DropdownMenuLabel>
                          <DropdownMenuItem asChild>
                            <Link to={`/admin/partners/edit/${partner.id}`} className="cursor-pointer flex items-center">
                              <Edit className="mr-2 h-4 w-4" /> Επεξεργασία
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setPartnerToDelete(partner)}
                            className="text-destructive focus:bg-destructive/10"
                          >
                            <Trash className="mr-2 h-4 w-4" /> Διαγραφή
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    {/* Contact info */}
                    <div className="flex flex-col gap-2 text-sm">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Phone className="h-4 w-4 text-slate-400" />
                        {partner.phone}
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Mail className="h-4 w-4 text-slate-400" />
                        {partner.email || <span className="text-slate-400">-</span>}
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex gap-2">
                      <Badge variant="secondary" className="bg-blue-50 text-blue-700 hover:bg-blue-100">
                        {partnerStatsById[partner.id]?.leads ?? 0} Leads
                      </Badge>
                      <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 hover:bg-emerald-100">
                        {partnerStatsById[partner.id]?.success ?? 0} Επιτυχίες
                      </Badge>
                      <Badge variant="secondary" className="bg-indigo-50 text-indigo-700 hover:bg-indigo-100">
                        {partnerStatsById[partner.id]?.conversion ?? 0}% Conversion
                      </Badge>
                      <Badge variant="secondary" className="bg-violet-50 text-violet-700 hover:bg-violet-100">
                        Health {partnerStatsById[partner.id]?.healthScore ?? 0}
                      </Badge>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {partnerStatsById[partner.id]?.needsAttention && (
                        <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50">
                          Χρειάζεται προσοχή
                        </Badge>
                      )}
                      {partner.sms_opt_out && (
                        <Badge variant="outline" className="text-slate-600">
                          SMS opt-out
                        </Badge>
                      )}
                      {(partnerStatsById[partner.id]?.stalePending ?? 0) > 0 && (
                        <Badge variant="outline" className="text-rose-700 border-rose-200 bg-rose-50">
                          {partnerStatsById[partner.id]?.stalePending} παλιά εκκρεμή
                        </Badge>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="rounded-md bg-slate-50 p-2 border border-slate-100">
                        <p className="text-slate-500">Νέα</p>
                        <p className="font-semibold text-slate-900">{partnerStatsById[partner.id]?.fresh ?? 0}</p>
                      </div>
                      <div className="rounded-md bg-slate-50 p-2 border border-slate-100">
                        <p className="text-slate-500">Εκκρεμή</p>
                        <p className="font-semibold text-slate-900">{partnerStatsById[partner.id]?.pending ?? 0}</p>
                      </div>
                      <div className="rounded-md bg-slate-50 p-2 border border-slate-100">
                        <p className="text-slate-500">Νέα 7 ημερών</p>
                        <p className="font-semibold text-slate-900">{partnerStatsById[partner.id]?.recent7d ?? 0}</p>
                      </div>
                      <div className="rounded-md bg-slate-50 p-2 border border-slate-100">
                        <p className="text-slate-500">View rate</p>
                        <p className="font-semibold text-slate-900">{partnerStatsById[partner.id]?.viewRate ?? 0}%</p>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span>Partner Health</span>
                        <span className={
                          (partnerStatsById[partner.id]?.healthScore ?? 0) >= 70
                            ? 'text-emerald-600 font-semibold'
                            : (partnerStatsById[partner.id]?.healthScore ?? 0) >= 45
                            ? 'text-amber-600 font-semibold'
                            : 'text-rose-600 font-semibold'
                        }>{partnerStatsById[partner.id]?.healthScore ?? 0}/100</span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className={`h-full transition-all duration-500 ${
                            (partnerStatsById[partner.id]?.healthScore ?? 0) >= 70
                              ? 'bg-emerald-500'
                              : (partnerStatsById[partner.id]?.healthScore ?? 0) >= 45
                              ? 'bg-amber-500'
                              : 'bg-rose-500'
                          }`}
                          style={{ width: `${partnerStatsById[partner.id]?.healthScore ?? 0}%` }}
                        />
                      </div>
                    </div>

                    {(partnerStatsById[partner.id]?.reasons.length ?? 0) > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {partnerStatsById[partner.id]?.reasons.slice(0, 2).map((reason) => (
                          <Badge key={`${partner.id}-${reason}`} variant="outline" className="text-xs text-amber-700 border-amber-200 bg-amber-50">
                            {reason}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {/* Services */}
                    <div className="flex flex-wrap gap-1.5">
                      {partner.services && partner.services.length > 0 ? (
                        partner.services.slice(0, 3).map((service, idx) => (
                          <ServiceBadge key={idx} service={service} />
                        ))
                      ) : (
                        <span className="text-xs text-slate-400">Χωρίς υπηρεσίες</span>
                      )}
                      {partner.services && partner.services.length > 3 && (
                        <Badge variant="outline" className="text-xs">+{partner.services.length - 3}</Badge>
                      )}
                    </div>
                  </CardContent>

                  <CardFooter className="pt-0">
                    <Link to={`/admin/partners/${partner.id}/dashboard`} className="w-full">
                      <Button variant="ghost" className="w-full justify-between text-blue-600 hover:text-blue-700 hover:bg-blue-50">
                        Προβολή στατιστικών
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </Link>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="inactive" className="mt-6">
          {inactivePartners.length === 0 ? (
            <EmptyState icon={<Users />} title="Δεν υπάρχουν ανενεργοί συνεργάτες" />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {inactivePartners.map((partner) => (
                <Card key={partner.id} className="border-slate-200 opacity-60 hover:opacity-100 transition-opacity">
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-300 flex items-center justify-center text-white font-bold text-sm">
                          {partner.name.charAt(0)}
                        </div>
                        <div>
                          <CardTitle className="text-lg">{partner.name}</CardTitle>
                          <CardDescription className="flex items-center gap-1 mt-0.5">
                            <MapPin className="h-3 w-3" />
                            {partner.location}
                          </CardDescription>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/admin/partners/edit/${partner.id}`}>
                              <Edit className="mr-2 h-4 w-4" /> Επεξεργασία
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setPartnerToDelete(partner)} className="text-destructive">
                            <Trash className="mr-2 h-4 w-4" /> Διαγραφή
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Badge variant="outline" className="text-slate-500">Ανενεργός</Badge>
                    <div className="mt-3 flex gap-2">
                      <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                        {partnerStatsById[partner.id]?.leads ?? 0} Leads
                      </Badge>
                      <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                        {partnerStatsById[partner.id]?.conversion ?? 0}% Conversion
                      </Badge>
                      <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                        Health {partnerStatsById[partner.id]?.healthScore ?? 0}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Delete Dialog */}
      <AlertDialog
        open={!!partnerToDelete}
        onOpenChange={(open) => !open && setPartnerToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Είστε βέβαιοι;</AlertDialogTitle>
            <AlertDialogDescription>
              Η ενέργεια αυτή θα διαγράψει μόνιμα τον συνεργάτη
              {partnerToDelete?.name && ` "${partnerToDelete.name}"`}.
              <br /><br />
              <span className="text-amber-600 font-medium">
                Συνεργάτες με leads δεν μπορούν να διαγραφούν.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Άκυρο</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePartner}
              className="bg-destructive hover:bg-destructive/90"
              disabled={isDeleting}
            >
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Διαγραφή
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Registration Link Dialog */}
      <Dialog open={showRegLinkDialog} onOpenChange={setShowRegLinkDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Σύνδεσμος Εγγραφής Συνεργατών</DialogTitle>
            <DialogDescription>
              Μοιραστείτε τον σύνδεσμο για αυτόματη εγγραφή συνεργατών.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Κωδικός εγγραφής:</strong> !Partner25
              </p>
            </div>
            <div className="flex gap-2">
              <Input
                readOnly
                value={`${window.location.origin}/partner/register`}
                className="bg-slate-50"
              />
              <Button onClick={copyRegistrationLink} className="shrink-0">
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Κλείσιμο</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Partners;
